import { useCallback, useState } from 'react';
import { markVouchersStale, useAppDispatch } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { DiscountType, FeatureFlagUseCase, RechargeDayType, ReleaseType, VoucherType, } from '@ibk/shared-types';
import { useActiveContractProducts, useBenefitContract } from '../../../hooks';
import { useUpdateVoucherBeneficiariesMutation, useUpdateVoucherContractDetailMutation, } from '../../../services/benefitApi';
import { BeneficiaryStatusInProduct } from '../../../types';
import { getUnformatedValue } from '../../../utils/beneficiaryUtils';
import { ParameterStep } from '../types';
function isNextButtonDisabled({ currentStep, formData, isBeneficiariesLoading, isFiltering, }) {
    if (currentStep === ParameterStep.GLOBAL_SETTINGS && formData) {
        if (formData.rechargeDayType === RechargeDayType.FIXED_DAY && !formData.rechargeDay) {
            return true;
        }
        return false;
    }
    if (currentStep === ParameterStep.BENEFICIARY_VALUES) {
        if (isBeneficiariesLoading || isFiltering) {
            return true;
        }
        return false;
    }
    return false;
}
export function useParametersState(voucherType = VoucherType.VR) {
    const dispatch = useAppDispatch();
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isBeneficiaryDiscountEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_ENABLE_BENEFICIARY_DISCOUNT, undefined);
    const { contractData, isLoading: isLoadingContract } = useBenefitContract();
    const voucherContractId = contractData?.id ?? '';
    const { products, isLoading: isLoadingProducts } = useActiveContractProducts({
        voucherContractId,
    });
    const [updateVoucherContractDetail] = useUpdateVoucherContractDetailMutation();
    const [updateVoucherBeneficiaries] = useUpdateVoucherBeneficiariesMutation();
    const voucher = products?.find(product => product.voucherTypeDescription === voucherType);
    const [showAlert, setShowAlert] = useState(true);
    const [showStep2Alert, setShowStep2Alert] = useState(true);
    const [currentStep, setCurrentStep] = useState(ParameterStep.GLOBAL_SETTINGS);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [benefitType] = useState(voucherType);
    const [isBeneficiariesLoading, setIsBeneficiariesLoading] = useState(false);
    const [isFiltering, setIsFiltering] = useState(false);
    const [isEmptyState, setIsEmptyState] = useState(false);
    const handleCloseAlert = useCallback(() => {
        setShowAlert(false);
    }, []);
    const handleCloseStep2Alert = useCallback(() => {
        setShowStep2Alert(false);
    }, []);
    const handleNextStep = useCallback(() => {
        if (currentStep === ParameterStep.GLOBAL_SETTINGS) {
            setCurrentStep(ParameterStep.BENEFICIARY_VALUES);
        }
        else if (currentStep === ParameterStep.BENEFICIARY_VALUES) {
            setCurrentStep(ParameterStep.FEEDBACK);
        }
    }, [currentStep]);
    const handlePreviousStep = useCallback(() => {
        if (currentStep === ParameterStep.BENEFICIARY_VALUES) {
            setCurrentStep(ParameterStep.GLOBAL_SETTINGS);
        }
    }, [currentStep]);
    const handleCloseErrorModal = useCallback(() => {
        setShowErrorModal(false);
    }, []);
    const handleBeneficiariesLoading = useCallback((loading) => {
        setIsBeneficiariesLoading(loading);
    }, []);
    const handleFiltering = useCallback((filtering) => {
        setIsFiltering(filtering);
    }, []);
    const handleEmptyStateChange = useCallback((isEmpty) => {
        setIsEmptyState(isEmpty);
    }, []);
    const buildVoucherContractDetailBody = useCallback((updateVoucherBody, formData, step2FormData) => {
        const baseBody = {
            deliveryModel: updateVoucherBody.deliveryModel,
            rechargeDay: formData.rechargeDay ?? null,
            rechargeDayType: formData.rechargeDayType,
            discountType: formData.payrollDeduction ? formData.discountType : DiscountType.NO_DISCOUNT,
            discountValue: formData.discountValue ? getUnformatedValue(formData.discountValue) : null,
            releaseType: step2FormData.releaseType,
        };
        if (step2FormData.releaseType === ReleaseType.MONTHLY) {
            baseBody.baseValue = getUnformatedValue(step2FormData.globalValue);
            baseBody.amountDaily = undefined;
        }
        else {
            baseBody.amountDaily = getUnformatedValue(step2FormData.globalValue);
            baseBody.baseValue = undefined;
        }
        if (isBeneficiaryDiscountEnabled) {
            baseBody.isIndividualDiscount = formData.isIndividualDiscount ?? false;
        }
        return baseBody;
    }, [isBeneficiaryDiscountEnabled]);
    const buildVoucherBeneficiariesBody = useCallback((step2FormData, formData) => {
        const shouldIncludeDiscountValue = isBeneficiaryDiscountEnabled && formData.payrollDeduction === true;
        return {
            beneficiaries: (step2FormData.selectedBeneficiaries ?? []).map(selected => {
                const beneficiary = {
                    contractBeneficiary: selected.beneficiaryId,
                    status: BeneficiaryStatusInProduct.ACTIVE,
                    value: selected.value,
                };
                if (shouldIncludeDiscountValue && selected.discountValue !== undefined) {
                    beneficiary.discountValue = selected.discountValue;
                }
                return beneficiary;
            }),
        };
    }, [isBeneficiaryDiscountEnabled]);
    const handleUpdateVoucherContractDetail = useCallback(async (formData, step2FormData) => {
        if (voucher) {
            setShowSuccessModal(false);
            setShowErrorModal(false);
            const { voucherType } = voucher;
            try {
                await updateVoucherContractDetail({
                    voucherContractId,
                    voucherType,
                    body: buildVoucherContractDetailBody(voucher, formData, step2FormData),
                }).unwrap();
                await updateVoucherBeneficiaries({
                    voucherContractId,
                    voucherTypeId: voucherType,
                    body: buildVoucherBeneficiariesBody(step2FormData, formData),
                }).unwrap();
                dispatch(markVouchersStale({ contractId: voucherContractId }));
                setShowSuccessModal(true);
            }
            catch (error) {
                console.error('Error updating voucher contract detail:', error);
                setShowErrorModal(true);
            }
        }
    }, [
        dispatch,
        updateVoucherContractDetail,
        updateVoucherBeneficiaries,
        voucherContractId,
        voucher,
        buildVoucherContractDetailBody,
        buildVoucherBeneficiariesBody,
    ]);
    return {
        state: {
            showAlert,
            showStep2Alert,
            currentStep,
            showSuccessModal,
            showErrorModal,
            benefitType,
            isLoading: isLoadingContract || isLoadingProducts,
            voucherContractId,
            voucher,
            isBeneficiariesLoading,
            isBeneficiaryDiscountEnabled,
            isFiltering,
            isEmptyState,
        },
        actions: {
            handleCloseAlert,
            handleCloseStep2Alert,
            handleNextStep,
            handlePreviousStep,
            handleCloseErrorModal,
            handleBeneficiariesLoading,
            handleFiltering,
            handleEmptyStateChange,
            handleUpdateVoucherContractDetail,
        },
        utils: {
            isNextButtonDisabled,
        },
    };
}
